import React from 'react';
import {
  C,
  CARD_W,
  CIRCLE_R,
  PLUS_R,
  BRANCH_STEM,
  CARD_GAP,
} from '../flowCanvas.constants';
import ImageOverlayCardNode, { IMAGE_OVERLAY_CARD_H } from './ImageOverlayCardNode';

export default function ImageStackNode({
  step,
  stepX,
  stepCY,
  parentStep,
  parentStepX,
  parentStepY,
  items,
  activeStepId,
  activeGraphicIndex,
  assetFolder,
  campaignId,
  onImageClick,
  onAddImageClick,
  onImageFieldChange,
  onRemoveImage,
  visualStyle = 'default',
  typeLabel = 'Image',
  behaviourLabel,
  showPositionInputs = false,
}) {
  const parentRadius = parentStep?.nodeConfig?.nodeStyle === 'infoButton' ? PLUS_R : CIRCLE_R;
  const hasParent = parentStepX !== null && parentStepX !== undefined;

  const count = Math.max(items.length, 1);
  const totalH = count * (IMAGE_OVERLAY_CARD_H + CARD_GAP) - CARD_GAP;

  const cardCentreX = stepX;
  const cardLeftX = cardCentreX - CARD_W / 2;
  const sourceX = hasParent ? parentStepX + parentRadius : cardLeftX - BRANCH_STEM;
  const sourceY = parentStepY ?? stepCY;
  // Always centre the stack on stepCY so cards grow equally above and below the wire.
  const stackTopY = stepCY - totalH / 2;
  const stackMidY = stepCY;
  // branchMidX: vertical spine entry point left of cards
  const branchMidX = cardLeftX - 18;
  // plusX: + button centre, far enough left that right edge clears branchMidX
  // plusX + PLUS_R = branchMidX - 16 (16px stub visible between button and spine)
  const plusX = branchMidX - PLUS_R - 16;
  const plusY = stackMidY;
  const firstCardY = stackTopY + IMAGE_OVERLAY_CARD_H / 2;
  const lastCardY = stackTopY + (count - 1) * (IMAGE_OVERLAY_CARD_H + CARD_GAP) + IMAGE_OVERLAY_CARD_H / 2;
  const branchColor = items.length > 0 ? C.purple : C.orange;
  const addButtonLabel = step.customisationConfig?.meta?.addButtonLabel || 'Add Image';
  const cardLabel = step.customisationConfig?.variables?.[0]?.displayLabel || step.stepTitle || 'Images';
  const fallbackTitleBase = showPositionInputs ? 'Overlay Image' : 'Image';

  return (
    <>
      {hasParent && (
        <g style={{ pointerEvents: 'none' }}>
          {/* When parent is at a different Y: rise/drop vertically at sourceX first,
              then run horizontally across to the button and spine.
              When parent is at the same Y: no vertical, just horizontal. */}
          {Math.abs(sourceY - stackMidY) > 2 && (
            <line
              x1={sourceX}
              y1={sourceY}
              x2={sourceX}
              y2={stackMidY}
              stroke={branchColor}
              strokeWidth="1.5"
              strokeOpacity="0.78"
              strokeLinecap="square"
            />
          )}
          {sourceX < plusX - PLUS_R && (
            <line
              x1={sourceX}
              y1={stackMidY}
              x2={plusX - PLUS_R}
              y2={stackMidY}
              stroke={branchColor}
              strokeWidth="1.5"
              strokeOpacity="0.78"
              strokeLinecap="square"
            />
          )}
          {/* Stub from button right edge to branch spine */}
          <line
            x1={plusX + PLUS_R}
            y1={plusY}
            x2={branchMidX}
            y2={plusY}
            stroke={branchColor}
            strokeWidth="1.5"
            strokeOpacity="0.78"
            strokeLinecap="square"
          />
          {(count > 1 || Math.abs(stackMidY - firstCardY) > 2 || Math.abs(stackMidY - lastCardY) > 2) && (
            <line
              x1={branchMidX}
              y1={Math.min(firstCardY, stackMidY)}
              x2={branchMidX}
              y2={Math.max(lastCardY, stackMidY)}
              stroke={branchColor}
              strokeWidth="1.5"
              strokeOpacity="0.5"
            />
          )}
          {(items.length > 0 ? items : [null]).map((_, i) => {
            const cy = stackTopY + i * (IMAGE_OVERLAY_CARD_H + CARD_GAP) + IMAGE_OVERLAY_CARD_H / 2;
            return (
              <React.Fragment key={`spur-${i}`}>
                <line
                  x1={branchMidX}
                  y1={cy}
                  x2={cardLeftX}
                  y2={cy}
                  stroke={branchColor}
                  strokeWidth="1.5"
                  strokeOpacity="0.78"
                  strokeLinecap="square"
                />
                <circle cx={cardLeftX} cy={cy} r="3" fill={branchColor} fillOpacity="0.9" />
              </React.Fragment>
            );
          })}
          <text
            x={plusX}
            y={plusY - PLUS_R - 8}
            textAnchor="middle"
            fontSize="9.5"
            fontWeight="600"
            fill={C.green}
            style={{ userSelect: 'none' }}
          >
            {addButtonLabel}
          </text>
        </g>
      )}

      {hasParent && (
        <g onClick={onAddImageClick} style={{ cursor: 'pointer' }}>
          <circle cx={plusX} cy={plusY} r={PLUS_R} fill={C.green} />
          <text
            x={plusX}
            y={plusY + 1}
            textAnchor="middle"
            dominantBaseline="middle"
            fontSize="16"
            fontWeight="300"
            fill="#fff"
            style={{ pointerEvents: 'none', userSelect: 'none' }}
          >+
          </text>
        </g>
      )}

      {items.length > 0 ? items.map((item, index) => {
        const cardY = stackTopY + index * (IMAGE_OVERLAY_CARD_H + CARD_GAP) + IMAGE_OVERLAY_CARD_H / 2;
        const isSelected = activeStepId === step.stepId && activeGraphicIndex === index;
        return (
          <ImageOverlayCardNode
            key={`${step.stepId}-${index}`}
            item={item}
            index={index}
            selected={isSelected}
            onClick={() => onImageClick(index)}
            onDelete={onRemoveImage ? () => onRemoveImage(index) : null}
            onFieldChange={onImageFieldChange ? (field, value) => onImageFieldChange(step, index, field, value) : null}
            overrideX={cardCentreX}
            overrideY={cardY}
            assetFolder={assetFolder}
            campaignId={campaignId}
            visualStyle={visualStyle}
            label={cardLabel}
            fallbackTitleBase={fallbackTitleBase}
            typeLabel={typeLabel}
            behaviourLabel={behaviourLabel}
            showPositionInputs={showPositionInputs}
          />
        );
      }) : (
        <g key="empty" onClick={onAddImageClick} style={{ cursor: 'pointer' }}>
          <rect x={cardCentreX - CARD_W / 2 + 2} y={stepCY - IMAGE_OVERLAY_CARD_H / 2 + 4}
            width={CARD_W} height={IMAGE_OVERLAY_CARD_H} rx="8" fill="#000" fillOpacity="0.3" />
          <rect x={cardCentreX - CARD_W / 2} y={stepCY - IMAGE_OVERLAY_CARD_H / 2}
            width={CARD_W} height={IMAGE_OVERLAY_CARD_H} rx="8"
            fill={C.card} stroke={C.orange} strokeWidth="1.5" strokeDasharray="5 3" />
          <text x={cardCentreX} y={stepCY - 18} textAnchor="middle" fontSize="11" fontWeight="600" fill={C.orange}
            style={{ pointerEvents: 'none', userSelect: 'none' }}>No images yet</text>
          <text x={cardCentreX} y={stepCY + 6} textAnchor="middle" fontSize="20" fontWeight="300" fill="#4ade80"
            style={{ pointerEvents: 'none', userSelect: 'none' }}>+</text>
          <text x={cardCentreX} y={stepCY + 28} textAnchor="middle" fontSize="9" fill={C.textDim}
            style={{ pointerEvents: 'none', userSelect: 'none' }}>Click to add an image</text>
        </g>
      )}
    </>
  );
}
